import * as React from 'react';
import { useRef, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { findHomeUndiagnosedPreviewFirstDay } from '@lean-body/src/domain/daily_lesson';
import { colorForVolumeNumber } from '@lean-body/src/domain/daily_lesson_volume_colors';
import { HomeDailyLessonItemsGroup } from '../molecules/home_daily_lesson_items_group';
import { HomeDailyLessonVolumeDivider } from '../molecules/home_daily_lesson_volume_divider';
import { HomeDailyLessonPopoverLayer } from './home_daily_lesson_popover_layer';
const ICON_CYCLE = ['dumbbell', 'flower', 'note', 'dumbbell', 'note', 'sun', 'flower'];
export const HomeDailyLessonMap = ({ dailyLesson, onStartDay, onChangeDay, onDiagnosisStart, isRediagnosisTarget, popoverLayerRef, classObject, }) => {
    var _a, _b, _c;
    const mapShellRef = useRef(null);
    const currentDay = dailyLesson.currentDay();
    const rediagnosisDay = isRediagnosisTarget ? currentDay : null;
    const diagnosisDay = (_a = findHomeUndiagnosedPreviewFirstDay(dailyLesson)) !== null && _a !== void 0 ? _a : rediagnosisDay;
    const diagnosisDayId = (_b = diagnosisDay === null || diagnosisDay === void 0 ? void 0 : diagnosisDay.id) !== null && _b !== void 0 ? _b : null;
    const isRediagnosisPopover = rediagnosisDay != null;
    const initialDay = diagnosisDay !== null && diagnosisDay !== void 0 ? diagnosisDay : currentDay;
    const initialKey = initialDay != null ? `day-${initialDay.id}` : null;
    const [selectedKey, setSelectedKey] = useState(() => initialKey);
    const [renderedKey, setRenderedKey] = useState(() => initialKey);
    let mapItemIndexOffset = 0;
    const volumes = dailyLesson.volumes;
    const lastVolume = volumes.length > 0 ? volumes[volumes.length - 1] : null;
    const isMapItemAlignRight = (mapItemIndex) => mapItemIndex % 2 === 1;
    const getIconForMapItemIndex = (mapItemIndex) => ICON_CYCLE[mapItemIndex % ICON_CYCLE.length];
    const onClickItem = (key) => {
        if (selectedKey === key) {
            setSelectedKey(null);
            return;
        }
        setSelectedKey(key);
        setRenderedKey(key);
    };
    // 過去から現在までのボリュームを作成する
    const buildVolumeItems = (volume, mapItemIndexOffset) => {
        const items = volume.days.map((day, index) => {
            const state = day.isCurrent ? 'current' : 'completed';
            const mapItemIndex = mapItemIndexOffset + index;
            const key = `day-${day.id}`;
            return {
                key,
                icon: getIconForMapItemIndex(mapItemIndex),
                state,
                alignRight: isMapItemAlignRight(mapItemIndex),
                day,
                onClick: () => onClickItem(key),
            };
        });
        // 現在のボリュームではない場合は全て揃っているはずなのでそのまま返す
        if (!volume.isCurrentVolume) {
            return items;
        }
        const futureDaysInVolume = volume.days.filter((day) => !day.isCompleted && !day.isCurrent).length;
        const extraGrayCount = Math.max(0, dailyLesson.currentVolumeRemainingLessonCount - futureDaysInVolume - 1);
        // 現在レッスン以降の不足分を追加する
        for (let i = 0; i < extraGrayCount; i++) {
            const index = volume.days.length + i;
            const mapItemIndex = mapItemIndexOffset + index;
            const key = `future-${volume.id}-${index}`;
            items.push({
                key,
                icon: getIconForMapItemIndex(mapItemIndex),
                state: 'future',
                alignRight: isMapItemAlignRight(mapItemIndex),
                onClick: () => onClickItem(key),
            });
        }
        return items;
    };
    const volumeBlocks = volumes.map((volume) => {
        const items = buildVolumeItems(volume, mapItemIndexOffset);
        mapItemIndexOffset += items.length;
        return {
            key: `volume-${volume.id}`,
            volumeNumber: volume.volumeNumber,
            volumeColor: colorForVolumeNumber(volume.volumeNumber),
            items,
            isFirstVolume: volume.volumeNumber === 1,
        };
    });
    // 次の各ボリュームの駒を作成する
    const buildNextVolumeItems = (volumeNumber, lessonCount, mapItemIndexOffset) => {
        return Array.from({ length: lessonCount }, (_, index) => {
            const mapItemIndex = mapItemIndexOffset + index;
            const key = `next-vol-${volumeNumber}-${index}`;
            return {
                key,
                icon: getIconForMapItemIndex(mapItemIndex),
                state: 'future',
                alignRight: isMapItemAlignRight(mapItemIndex),
                onClick: () => onClickItem(key),
            };
        });
    };
    // 現在以降の未来ボリューム（複数件）を nextVolumeLessonCounts のレッスン数分作成する
    const lastVolumeNumber = lastVolume ? lastVolume.volumeNumber : 0;
    const nextVolumeBlocks = dailyLesson.nextVolumeLessonCounts.map((lessonCount, index) => {
        const volumeNumber = lastVolumeNumber + 1 + index;
        const items = buildNextVolumeItems(volumeNumber, lessonCount, mapItemIndexOffset);
        mapItemIndexOffset += items.length;
        return { key: `next-vol-${volumeNumber}`, volumeNumber, items };
    });
    const allItems = [
        ...volumeBlocks.flatMap((block) => block.items),
        ...nextVolumeBlocks.flatMap((block) => block.items),
    ];
    const renderedItem = renderedKey != null ? ((_c = allItems.find((item) => item.key === renderedKey)) !== null && _c !== void 0 ? _c : null) : null;
    return (React.createElement("div", { className: css(styles.mapShell, classObject), ref: mapShellRef },
        volumeBlocks.map((block) => (React.createElement(React.Fragment, { key: block.key },
            React.createElement(HomeDailyLessonVolumeDivider, { volumeNumber: block.volumeNumber, isFirstVolume: block.isFirstVolume }),
            React.createElement(HomeDailyLessonItemsGroup, { volumeColor: block.volumeColor, items: block.items, selectedItemKey: selectedKey })))),
        nextVolumeBlocks
            .filter((block) => block.items.length > 0)
            .map((block) => (React.createElement(React.Fragment, { key: block.key },
            React.createElement(HomeDailyLessonVolumeDivider, { volumeNumber: block.volumeNumber, isFirstVolume: false }),
            React.createElement(HomeDailyLessonItemsGroup, { volumeColor: colorForVolumeNumber(block.volumeNumber), items: block.items, selectedItemKey: selectedKey })))),
        React.createElement(HomeDailyLessonPopoverLayer, { mapShellRef: mapShellRef, popoverLayerRef: popoverLayerRef, selectedKey: selectedKey, renderedKey: renderedKey, renderedItem: renderedItem, setSelectedKey: setSelectedKey, setRenderedKey: setRenderedKey, diagnosisDayId: diagnosisDayId, isRediagnosisPopover: isRediagnosisPopover, onStartDay: onStartDay, onChangeDay: onChangeDay, onDiagnosisStart: onDiagnosisStart })));
};
const styles = StyleSheet.create({
    mapShell: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 375,
        maxWidth: '100%',
        flexShrink: 0,
        alignSelf: 'center',
    },
});
